import { ReactNode } from "react";
import {
  ChatWidgetAction,
  ChatWidgetStatus,
  ChatWidgetText,
  ChatWidgetValues,
  isSafeWidgetImage,
  isSafeWidgetUrl,
  StatusWidget,
} from "../../Helper/ChatWidget";
import Communicator from "../../Helper/Communicator";
import { CheckIcon, DashIcon, StarIcon } from "./ChatWidgetIcons";
// Shared styles load before every card's own SCSS (cards import this file
// first), so a card rule overrides a primitive at equal specificity.
import "./ChatWidget.scss";

// Shared building blocks of the chat cards: the card surface, tone pills,
// buttons, the answered receipt line, meters, the status timeline, the
// segmented order progress and image thumbnails. Card files compose these.

export type ChatWidgetAnswer = (
  action: ChatWidgetAction,
  values: ChatWidgetValues,
  label: string,
  /** `undo`: the confirm receipt's Undo; `followUp`: a second answer the server asked for (Calendly confirmation). */
  options?: { kind?: "undo" | "followUp" },
) => void;

/**
 * Opens a card link in a new tab (https only; the server already enforces it).
 * Card links come from agents: session placeholders are never filled.
 */
export const openWidgetUrl = (url?: string) => {
  if (isSafeWidgetUrl(url))
    Communicator.openURL(url, true, { placeholders: false });
};

export const cx = (...classes: (string | false | undefined | null)[]) =>
  classes.filter(Boolean).join(" ");

export const Card = ({
  className,
  children,
  label,
}: {
  className?: string;
  children: ReactNode;
  /** Accessible name of the card region. */
  label?: string;
}) => (
  <article className={cx("cw-card", className)} aria-label={label}>
    {children}
  </article>
);

export const Pill = ({
  status,
  withDot = true,
  className,
}: {
  status?: ChatWidgetStatus;
  withDot?: boolean;
  className?: string;
}) =>
  status?.label ? (
    <span
      className={cx(
        "cw-pill",
        `cw-tone--${status.tone ?? "neutral"}`,
        className,
      )}
    >
      {withDot && <span className="cw-pill-dot" aria-hidden="true" />}
      {status.label}
    </span>
  ) : null;

export const Eyebrow = ({ children }: { children: ReactNode }) => (
  <span className="cw-eyebrow">{children}</span>
);

export const Button = ({
  variant = "primary",
  className,
  children,
  onClick,
  disabled,
  type = "button",
  ariaLabel,
}: {
  variant?: "primary" | "ghost" | "danger";
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  ariaLabel?: string;
}) => (
  <button
    type={type}
    className={cx("cw-btn", `cw-btn--${variant}`, className)}
    onClick={onClick}
    disabled={disabled}
    aria-label={ariaLabel}
  >
    {children}
  </button>
);

/** Button row at the bottom of a card. */
export const Foot = ({
  children,
  flush,
}: {
  children: ReactNode;
  /** No top padding: the row follows a padded block directly. */
  flush?: boolean;
}) => (
  <div className={cx("cw-foot", flush && "cw-foot--flush")}>{children}</div>
);

/** Full-width accent link along the card's bottom edge ("Manage billing ↗"). */
export const LinkFoot = ({
  url,
  children,
}: {
  url?: string;
  children: ReactNode;
}) =>
  isSafeWidgetUrl(url) ? (
    <button
      type="button"
      className="cw-linkfoot"
      onClick={() => openWidgetUrl(url)}
    >
      {children}
    </button>
  ) : null;

const relativeTime = (at: string | undefined, text: ChatWidgetText) => {
  if (!at) return text("widgetJustNow");
  const date = new Date(at);
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (!Number.isFinite(minutes) || minutes < 1) return text("widgetJustNow");
  if (minutes < 60) return text("widgetMinutesAgo", { n: minutes });
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const receiptTime = relativeTime;

/**
 * The answered state of an interactive card: one line with a check tile, what
 * happened, a detail, and an optional ghost action (Undo).
 */
export const Receipt = ({
  title,
  detail,
  tone = "success",
  action,
  isDetailStruck,
}: {
  title: string;
  detail?: string;
  /** `pending`: a spinner while the server works (booking). */
  tone?: "success" | "neutral" | "pending";
  action?: { label: string; onClick: () => void };
  isDetailStruck?: boolean;
}) => (
  <Card className="cw-receipt">
    <div className="cw-receipt-row" role="status">
      <span
        className={cx("cw-receipt-tile", `cw-receipt-tile--${tone}`)}
        aria-hidden="true"
      >
        {tone === "success" && <CheckIcon size={15} strokeWidth={2.8} />}
        {tone === "neutral" && <DashIcon size={15} strokeWidth={2.8} />}
        {tone === "pending" && <span className="cw-spinner" />}
      </span>
      <span className="cw-receipt-text">
        <span className="cw-receipt-title">{title}</span>
        {detail &&
          (isDetailStruck ? (
            <s className="cw-receipt-detail">{detail}</s>
          ) : (
            <span className="cw-receipt-detail">{detail}</span>
          ))}
      </span>
      {action && (
        <button
          type="button"
          className="cw-btn cw-btn--ghost cw-btn--small"
          onClick={action.onClick}
        >
          {action.label}
        </button>
      )}
    </div>
  </Card>
);

const formatCount = (value: number) =>
  Number.isFinite(value) ? value.toLocaleString() : "";

export const Meter = ({
  label,
  used,
  limit,
  unit,
  text,
}: {
  label: string;
  used: number;
  limit: number;
  unit?: string;
  text: ChatWidgetText;
}) => {
  const percent =
    limit > 0 ? Math.max(0, Math.min(100, (used / limit) * 100)) : 0;
  const amount = text("widgetMeterOf", {
    used: formatCount(used),
    limit: formatCount(limit),
  });
  return (
    <div className="cw-meter">
      <div className="cw-row cw-sm">
        <span>{label}</span>
        <span className="cw-num cw-muted">
          {amount}
          {unit ? ` ${unit}` : ""}
        </span>
      </div>
      <div
        className="cw-meter-track"
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={limit}
        aria-valuenow={used}
      >
        <div className="cw-meter-fill" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
};

export const Timeline = ({ steps }: { steps: StatusWidget["steps"] }) => (
  <ol className="cw-timeline">
    {steps.map((step, index) => {
      const isLast = index === steps.length - 1;
      return (
        <li
          key={index}
          className={cx("cw-tl-step", `cw-tl-step--${step.state}`)}
        >
          <span className="cw-tl-rail" aria-hidden="true">
            <span className="cw-tl-node">
              {step.state === "done" && <CheckIcon size={10} strokeWidth={4} />}
              {step.state === "current" && <span className="cw-tl-node-dot" />}
              {step.state === "failed" && (
                <DashIcon size={10} strokeWidth={4} />
              )}
            </span>
            {!isLast && <span className="cw-tl-bar" />}
          </span>
          <span className={cx("cw-tl-text", !isLast && "cw-tl-text--spaced")}>
            <span className="cw-tl-label">{step.label}</span>
            {(step.date || step.detail) && (
              <span className="cw-tl-meta cw-num">
                {[step.date, step.detail].filter(Boolean).join(" · ")}
              </span>
            )}
          </span>
        </li>
      );
    })}
  </ol>
);

/** Segmented progress (order): done segments, a half-filled one in transit. */
export const Segments = ({
  steps,
  current,
  partial,
}: {
  steps: string[];
  current: number;
  partial?: boolean;
}) => {
  const columns = {
    gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`,
  };
  return (
    <div className="cw-segments">
      <div className="cw-seg-bars" style={columns} aria-hidden="true">
        {steps.map((_, index) => (
          <span
            key={index}
            className={cx(
              "cw-seg",
              (index < current || (index === current && !partial)) &&
                "cw-seg--on",
              index === current && partial && "cw-seg--half",
            )}
          />
        ))}
      </div>
      <ol className="cw-seg-labels" style={columns}>
        {steps.map((label, index) => (
          <li
            key={index}
            className={cx(
              index <= current && "cw-seg-label--reached",
              index === current && "cw-seg-label--current",
            )}
            aria-current={index === current ? "step" : undefined}
          >
            {label}
          </li>
        ))}
      </ol>
    </div>
  );
};

/** Square image tile (bag, return, order lines); a neutral box without an image. */
export const Thumb = ({
  image,
  size = 44,
  className,
}: {
  image?: string;
  size?: number;
  className?: string;
}) => (
  <span
    className={cx("cw-thumb", className)}
    style={{ width: size, height: size }}
    aria-hidden="true"
  >
    {isSafeWidgetImage(image) && (
      <img src={image} alt="" loading="lazy" referrerPolicy="no-referrer" />
    )}
  </span>
);

/** "4.8 · 312" with a star. */
export const Rating = ({
  rating,
  reviewCount,
}: {
  rating?: number;
  reviewCount?: number;
}) =>
  typeof rating === "number" ? (
    <span className="cw-rating cw-sm cw-muted">
      <span className="cw-rating-star">
        <StarIcon />
      </span>
      <span className="cw-num">
        {rating.toFixed(1)}
        {typeof reviewCount === "number"
          ? ` · ${reviewCount.toLocaleString()}`
          : ""}
      </span>
    </span>
  ) : null;
